import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function HrOverview() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [error, setError] = useState('');

  const authCheck = useCallback((res) => {
    if (res.status === 401 || res.status === 403) { navigate('/login'); throw new Error('Unauthorized'); }
    return res;
  }, [navigate]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/companies/${companyId}/hr-overview`, { headers: getAuthHeaders() });
      authCheck(res);
      const json = await res.json();
      setData(json);
    } catch (e) {
      if (e.message !== 'Unauthorized') setError('Failed to load HR Overview');
    } finally { setLoading(false); }
  }, [companyId, authCheck]);

  useEffect(() => { if (companyId) fetchOverview(); }, [companyId, fetchOverview]);

  // ─── STYLES ───
  const containerStyle = { padding: '24px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', maxWidth: '1400px', margin: '0 auto' };
  const titleStyle = { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: '0 0 24px' };
  const sectionTitle = { fontSize: '16px', fontWeight: '700', color: '#1e293b', margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '8px' };
  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' };
  const sectionStyle = { marginBottom: '28px' };

  const metricCard = (label, value, color = '#3b82f6', link = null) => (
    <div
      onClick={() => link && navigate(link)}
      style={{
        background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '16px',
        cursor: link ? 'pointer' : 'default', transition: 'box-shadow 0.2s',
      }}
      onMouseEnter={e => { if (link) e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>{label}</div>
      <div style={{ fontSize: '22px', fontWeight: '700', color }}>{value}</div>
    </div>
  );

  const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const severityColor = { error: '#ef4444', warning: '#f59e0b', info: '#3b82f6' };
  const typeIcon = { employee: '👤', organization: '🏢', attendance: '📋', leave: '🏖️', payroll: '💰', insurance: '🛡️', employee_added: '➕', leave_approved: '✅', employee_end: '📅', insurance_expiry: '⚠️' };

  if (initialLoading) return <div style={containerStyle}><p>Loading HR Overview...</p></div>;
  if (error) return <div style={containerStyle}><p style={{ color: '#ef4444' }}>{error}</p></div>;
  if (!data) return <div style={containerStyle}><p>No data available</p></div>;

  return (
    <div style={containerStyle}>
      <h1 style={titleStyle}>HR Overview</h1>

      {/* ═══ EMPLOYEE OVERVIEW ═══ */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>👤 Employee Overview</h2>
        <div style={gridStyle}>
          {metricCard('Total Employees', data.employees?.total, '#1e293b', '/company/employees')}
          {metricCard('Active', data.employees?.active, '#10b981', '/company/employees')}
          {metricCard('On Hold', data.employees?.on_hold, '#f59e0b', '/company/employees')}
          {metricCard('New (30 days)', data.employees?.new_employees, '#3b82f6', '/company/employees')}
          {metricCard('Scheduled to Leave', data.employees?.scheduled_to_leave, '#ef4444')}
          {metricCard('Archived', data.employees?.archived, '#6b7280')}
        </div>
      </div>

      {/* ═══ ORGANIZATION OVERVIEW ═══ */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>🏢 Organization</h2>
        <div style={gridStyle}>
          {metricCard('Total Departments', data.organization?.total_departments, '#1e293b')}
          {metricCard('Without Managers', data.organization?.departments_without_managers, data.organization?.departments_without_managers > 0 ? '#ef4444' : '#10b981')}
        </div>
      </div>

      {/* ═══ ATTENDANCE OVERVIEW ═══ */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>📋 Attendance</h2>
        <div style={gridStyle}>
          {metricCard('On Site', data.attendance?.on_site, '#10b981', '/company/attendance')}
          {metricCard('Remote', data.attendance?.remote, '#3b82f6', '/company/attendance')}
          {metricCard('Missing', data.attendance?.missing_attendance, data.attendance?.missing_attendance > 0 ? '#ef4444' : '#10b981', '/company/attendance')}
          {metricCard('Approved OT', data.attendance?.approved_overtime, '#8b5cf6', '/company/attendance')}
          {metricCard('Pending OT', data.attendance?.pending_overtime, data.attendance?.pending_overtime > 0 ? '#f59e0b' : '#10b981', '/company/attendance')}
        </div>
      </div>

      {/* ═══ LEAVE OVERVIEW ═══ */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>🏖️ Leave</h2>
        <div style={gridStyle}>
          {metricCard('Currently On Leave', data.leave?.currently_on_leave, '#3b82f6', '/company/leave-management')}
          {metricCard('Upcoming Leave', data.leave?.upcoming_leave, '#0ea5e9', '/company/leave-management')}
          {metricCard('Pending Requests', data.leave?.pending_requests, data.leave?.pending_requests > 0 ? '#f59e0b' : '#10b981', '/company/leave-management')}
          {metricCard('Negative Balances', data.leave?.negative_balances, data.leave?.negative_balances > 0 ? '#ef4444' : '#10b981', '/company/leave-management')}
        </div>
      </div>

      {/* ═══ PAYROLL OVERVIEW ═══ */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>💰 Payroll</h2>
        <div style={gridStyle}>
          {metricCard('Net Payroll', `$${fmt(data.payroll?.net_payroll)}`, '#059669', '/company/payroll')}
          {metricCard('Status', (data.payroll?.status || 'N/A').replace('_', ' '), '#3b82f6', '/company/payroll')}
          {metricCard('Total Overtime', `$${fmt(data.payroll?.total_overtime)}`, '#8b5cf6', '/company/payroll')}
          {metricCard('Total Transport', `$${fmt(data.payroll?.total_transportation)}`, '#f59e0b', '/company/payroll')}
          {metricCard('Additions', `$${fmt(data.payroll?.total_additions)}`, '#10b981', '/company/payroll')}
          {metricCard('Deductions', `$${fmt(data.payroll?.total_deductions)}`, '#ef4444', '/company/payroll')}
          {metricCard('Adjustments', data.payroll?.adjustments, data.payroll?.adjustments > 0 ? '#f59e0b' : '#10b981', '/company/payroll')}
          {metricCard('Issues', data.payroll?.issues, data.payroll?.issues > 0 ? '#ef4444' : '#10b981', '/company/payroll')}
        </div>
      </div>

      {/* ═══ INSURANCE OVERVIEW ═══ */}
      <div style={sectionStyle}>
        <h2 style={sectionTitle}>🛡️ Insurance</h2>
        <div style={gridStyle}>
          {metricCard('Active Policies', data.insurance?.active_policies, '#10b981', '/company/insurance')}
          {metricCard('Expiring Soon', data.insurance?.expiring_soon, data.insurance?.expiring_soon > 0 ? '#f59e0b' : '#10b981', '/company/insurance')}
          {metricCard('Expired', data.insurance?.expired_policies, data.insurance?.expired_policies > 0 ? '#ef4444' : '#10b981', '/company/insurance')}
          {metricCard('Upcoming Dues', data.insurance?.upcoming_dues, '#3b82f6', '/company/insurance')}
          {metricCard('Overdue Dues', data.insurance?.overdue_dues, data.insurance?.overdue_dues > 0 ? '#ef4444' : '#10b981', '/company/insurance')}
          {metricCard('Outstanding Balance', `$${fmt(data.insurance?.outstanding_balance)}`, '#f59e0b', '/company/insurance')}
        </div>
      </div>

      {/* ═══ PENDING ACTIONS & UPCOMING CHANGES ═══ */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px' }}>
        {/* Pending Actions */}
        <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <h2 style={sectionTitle}>⚡ Pending Actions ({data.pending_actions?.length || 0})</h2>
          {(!data.pending_actions || data.pending_actions.length === 0) && <p style={{ color: '#94a3b8', fontSize: '13px' }}>No pending actions — all clear!</p>}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {(data.pending_actions || []).map((action, i) => (
              <div
                key={i}
                onClick={() => action.link && navigate(action.link)}
                style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f1f5f9', cursor: action.link ? 'pointer' : 'default', fontSize: '13px' }}
              >
                <span>{typeIcon[action.type] || '📌'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ color: '#334155' }}>{action.message}</div>
                </div>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: severityColor[action.severity] || '#94a3b8', flexShrink: 0 }} />
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Changes */}
        <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px' }}>
          <h2 style={sectionTitle}>📅 Upcoming Changes</h2>
          {(!data.upcoming_changes || data.upcoming_changes.length === 0) && <p style={{ color: '#94a3b8', fontSize: '13px' }}>No upcoming changes</p>}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {(data.upcoming_changes || []).map((change, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                <span>{typeIcon[change.type] || '📌'}</span>
                <div style={{ flex: 1, color: '#334155' }}>{change.message}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ═══ RECENT ACTIVITY ═══ */}
      <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #e2e8f0', padding: '20px', marginBottom: '28px' }}>
        <h2 style={sectionTitle}>🕐 Recent Activity</h2>
        {(!data.recent_activity || data.recent_activity.length === 0) && <p style={{ color: '#94a3b8', fontSize: '13px' }}>No recent activity</p>}
        <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
          {(data.recent_activity || []).map((activity, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
              <span>{typeIcon[activity.type] || '📋'}</span>
              <div style={{ flex: 1, color: '#334155' }}>{activity.message}</div>
              <div style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{new Date(activity.date).toLocaleDateString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}